"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import { Heading } from "../design-system/atoms/Heading";
import type { BlockProps } from "./blocks/registry";

type StrapiMedia = {
  url?: string;
};

type AvisItem = {
  id?: number | string;
  nom?: string;
  ville?: string;
  description?: string;
  nbrEtoiles: number;
  image?: StrapiMedia;
};

type VisibleAvisItem = AvisItem & {
  realIndex: number;
};

type TemoignageData = {
  id?: number | string;
  documentId?: string;
  titre?: string;
  subTitle?: string;
  avis?: AvisItem[];
};

export default function Temoignage({ data }: BlockProps<TemoignageData>) {
  const [start, setStart] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false);

  const avis: AvisItem[] = Array.isArray(data?.avis) ? data.avis : [];
  const total = avis.length;

  //  Détection mobile et tablette
  useEffect(() => {
    const update = () => setIsTabletOrMobile(window.innerWidth < 1024);
    update();

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  if (!data) return null;
  if (total === 0) return null;



  const at = (offset: number): VisibleAvisItem => {
    const realIndex = (start + offset + total) % total;

    return {
      ...avis[realIndex],
      realIndex,
    };
  };

  const next = () => {
    setDirection(1);
    setStart((s) => (s + 1) % total);
  };

  const prev = () => {
    setDirection(-1);
    setStart((s) => (s - 1 + total) % total);
  };

  //  Responsive : 1 carte mobile, 3 cartes desktop
  const visibleCount = isTabletOrMobile ? 1 : Math.min(3, total);
  const visible = Array.from({ length: visibleCount }, (_, i) => at(i));

  const animationClass =
    direction === 1
      ? "animate-slide-in-right"
      : direction === -1
        ? "animate-slide-in-left"
        : "";

  return (
    <section className="py-16 bg-white lms-container" key={data.id}>
      {/* Titre */}
      <Heading
        as="h2"
        className="text-4xl lg:text-[44px] text-center font-bold text-text mb-6"
      >
        {data.titre}
      </Heading>

      {data.subTitle && (
        <p className="text-center text-2xl font-playfair text-primaryGreen pb-12">
          {data.subTitle}
        </p>
      )}

      {/* Zone carousel */}
      <div className="max-w-6xl mx-auto overflow-hidden">
        <div key={start} className={`transition-all duration-500 ease-out ${animationClass}`}>
          <div
            className={`grid grid-cols-1 gap-8 ${visibleCount === 3 ? "xl:grid-cols-3" : ""
              }`}
          >
            {visible.map((t) => (
              <article
                key={t.realIndex}
                className={`bg-white rounded-2xl p-6 text-center flex flex-col items-center shadow-md transition`}
              >
                {/* Avatar */}
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primaryGreen mb-4">
                  <Image
                    src={t?.image?.url || "https://fra1.digitaloceanspaces.com/site-lms/user_e013106616.png"}
                    alt={t?.nom || "Avatar"}
                    width={185}
                    height={184}
                  />
                </div>

                {/* Nom */}
                {t?.nom && <h3 className="font-bold text-lg">{t.nom}</h3>}

                {/* Sous-titre */}
                {t?.ville && (
                  <p className="text-gray-500 text-sm mb-4">{t.ville}</p>
                )}

                {/* Etoiles */}

                {t.nbrEtoiles && (
                  <div className="flex items-center mb-4">
                    {Array.from({ length: 5 }, (_, i) => (
                      <svg
                        key={i}
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-6 w-6 ${i < t.nbrEtoiles ? "text-yellow-400" : "text-gray-300"}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.974a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.39 2.462a1 1 0 00-.364 1.118l1.287 3.974c.3.921-.755 1.688-1.54 1.118l-3.39-2.462a1 1 0 00-1.175 0l-3.39 2.462c-.784.57-1.838-.197-1.539-1.118l1.286-3.974a1 1 0 00-.364-1.118L2.034 9.4c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.974z" />
                      </svg>
                    ))}
                  </div>
                )}


                {/* Avis */}
                {t?.description && (
                  <p className="text-text leading-relaxed">{t.description}</p>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* Boutons pagination */}
        <div className="flex justify-center gap-6 mt-10">
          <button
            onClick={prev}
            aria-label="Précédent"
            className="w-12 h-12 flex items-center justify-center border rounded-full text-text hover:bg-gray-100"
          >
            <Image width={20} height={20} src="https://site-lms.fra1.digitaloceanspaces.com/arrow_circle_left_9d8addf8e8.svg" alt="" className="w-5 h-5" />
          </button>

          <button
            onClick={next}
            aria-label="Suivant"
            className="w-12 h-12 flex items-center justify-center bg-secondary text-white rounded-full hover:bg-primaryGreen"
          >
            <Image width={20} height={20} src="https://site-lms.fra1.digitaloceanspaces.com/arrow_circle_right_514eb11600.svg" alt="" className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
